import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type ModeratorState =
  | { status: "loading" }
  | { status: "not-eligible"; reason: string }
  | { status: "eligible" }
  | { status: "pending" }
  | { status: "approved" }
  | { status: "rejected"; notes?: string | null };

export function useModeratorStatus() {
  const { session } = useAuth();
  const userId = session?.user?.id;

  return useQuery<ModeratorState>({
    queryKey: ["moderator-status", userId],
    enabled: !!userId,
    queryFn: async (): Promise<ModeratorState> => {
      if (!userId) return { status: "not-eligible", reason: "sign-in required" };

      // Fetch profile (tier + already-moderator)
      const { data: profile, error: profileErr } = await supabase
        .from("profiles")
        .select("subscription_tier, subscription_status, is_verified_moderator")
        .eq("id", userId)
        .maybeSingle();
      if (profileErr) throw profileErr;
      if (!profile) return { status: "not-eligible", reason: "profile not found" };

      if (profile.is_verified_moderator) return { status: "approved" };

      // Check most recent application
      const { data: app } = await supabase
        .from("moderator_applications")
        .select("status, reviewer_notes")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (app?.status === "pending") return { status: "pending" };
      if (app?.status === "rejected") return { status: "rejected", notes: app.reviewer_notes };
      // approved would imply is_verified_moderator=true above, but defensive:
      if (app?.status === "approved" && !profile.is_verified_moderator) {
        return { status: "approved" };
      }

      // Tier check
      const tierOk =
        (profile.subscription_tier === "seller" || profile.subscription_tier === "pro_seller") &&
        profile.subscription_status === "active";
      if (!tierOk) {
        return { status: "not-eligible", reason: "Requires an active Seller or Pro Seller subscription." };
      }

      // PASS-scan count check
      const { count, error: countErr } = await supabase
        .from("scans")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("verdict", "PASS")
        .eq("posted", true);
      if (countErr) throw countErr;

      if ((count ?? 0) < 10) {
        return {
          status: "not-eligible",
          reason: `Need 10 PASS-verified posts; you have ${count ?? 0}.`,
        };
      }

      return { status: "eligible" };
    },
    staleTime: 60_000, // refresh every minute
  });
}
